import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props { children: ReactNode; }
interface State { hasError: boolean; error?: Error; }

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info);
    // Auto-recover from stale chunk errors after a deploy by hard-reloading once.
    const msg = error?.message || "";
    const isChunkError =
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /Importing a module script failed/i.test(msg) ||
      /ChunkLoadError/i.test(msg) ||
      /Loading chunk [\d]+ failed/i.test(msg) ||
      /error loading dynamically imported module/i.test(msg);
    if (isChunkError) {
      const KEY = "__chunk_reload_at";
      const last = Number(sessionStorage.getItem(KEY) || 0);
      const now = Date.now();
      if (now - last > 10000) {
        sessionStorage.setItem(KEY, String(now));
        window.location.reload();
      }
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="font-display text-2xl font-bold mb-2">Something went wrong</h1>
          <p className="text-sm text-muted-foreground mb-6">
            {this.state.error?.message || "An unexpected error occurred. Please try reloading the page."}
          </p>
          <Button onClick={this.reset} className="gap-2">
            <RefreshCw className="w-4 h-4" /> Reload page
          </Button>
        </div>
      </div>
    );
  }
}
